import { useEffect } from "react";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import "./Viewport3DTakePropInspectLayer.css";
import { useDispatch, useSelector } from "react-redux";
import { useMapEvents } from "react-leaflet";
import { DomEvent } from "leaflet";
import { selectEnvironmentInteractionPoints } from "../../app/slices/EnvironmentSlice";
import { selectUsedProps } from "../../app/slices/PropLibrarySlice";
import {
  selectAllPropPresets,
  selectActors,
  selectSelectedTake3D,
  setSelectedTake,
  updateSelectedTake,
} from "../../app/slices/TrainingSlice";
import {
  getTakeStartPropCandidates,
  getTakeStartPropLabel,
  withTakeStartingProp,
} from "../../helpers/TakeStartingView";
import InteractionPointPreview, {
  PropPointStrategy,
} from "./InteractionPointPreview";
import PiSelect from "../../components/PiSelect";
import { useTranslation } from "react-i18next";

const defaultCameraColor = { r: 58 / 255, g: 196 / 255, b: 1, a: 1 };

/** Mounted only while editing a Take's Prop inspect starting view. */
export default function Viewport3DTakePropInspectLayer() {
  const dispatch = useDispatch();
  const take = useSelector(selectSelectedTake3D);
  const positions = useSelector(selectEnvironmentInteractionPoints);
  const props = useSelector(selectAllPropPresets);
  const usedProps = useSelector(selectUsedProps);
  const actors = useSelector(selectActors) ?? [];
  const { t } = useTranslation();
  const takeId = take?.id;

  useMapEvents({
    click: () => {
      if (takeId) dispatch(setSelectedTake(takeId));
    },
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented && takeId) {
        dispatch(setSelectedTake(takeId));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dispatch, takeId]);

  if (!take) return null;
  const candidates = getTakeStartPropCandidates(take, positions, props, usedProps);
  const groups = new Map<string, typeof candidates>();
  candidates.forEach((candidate) => {
    const key = candidate.position.id;
    groups.set(key, [...(groups.get(key) ?? []), candidate]);
  });
  const selectProp = (propId: string) => {
    const updated = withTakeStartingProp(take, propId);
    if (updated !== take) dispatch(updateSelectedTake(updated));
  };
  return (
    <>
      {Array.from(groups.values()).map((group) => {
        const active = group.find(({ prop }) => prop.id === take.startPropInspect?.propId);
        const { prop, position, usedProp, takeColor } = active ?? group[0];
        const selected = !!active;
        const options = group.map(({ prop, attachment }) => ({
          value: prop.id, label: getTakeStartPropLabel(prop, attachment, actors),
        }));
        const label = options.map((option) => option.label).join(", ");
        const color = takeColor ?? defaultCameraColor;
        return (
          <InteractionPointPreview
            key={`${position.id}:${label}`}
            strategy={PropPointStrategy}
            x={position.position.x}
            y={position.position.y}
            prop={usedProp}
            tooltipContent={label}
            title={label}
            popoverContent={group.length > 1 ? <PiSelect
              label={String(t("prop", "Prop"))}
              value={active?.prop.id ?? ""}
              options={options}
              placeholder={String(t("take_start_select_prop", "Select a Prop"))}
              onChange={selectProp}
            /> : undefined}
            badgeContent={selected ? <CameraAltOutlinedIcon fontSize="inherit" /> : undefined}
            badgeClassName="take-prop-inspect-camera-badge"
            badgeColor={color}
            markerBorderColor={color}
            markerBackgroundColor={selected ? color : undefined}
            markerIconColor={selected ? { r: 1, g: 1, b: 1, a: 1 } : undefined}
            directionDegrees={position.direction?.x}
            directionColor={color}
            onClick={(event) => {
              DomEvent.stopPropagation(event);
              if (group.length === 1) selectProp(prop.id);
            }}
          />
        );
      })}
    </>
  );
}
